import { useEffect, useState } from 'react';
import api from '@/services/api';
import { useAuth } from '@/state/auth/AuthProvider';
import { getBrowserTimeZone } from '@/utils/localePreferences';

/**
 * Resolved IANA timezone for scheduling and calendar payloads:
 * user profile `preferences.timezone` when authenticated and available, else browser timezone.
 */
export function useUserTimeZone(): { timeZone: string; loading: boolean } {
  const { isAuthenticated } = useAuth();
  const [timeZone, setTimeZone] = useState(getBrowserTimeZone);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setTimeZone(getBrowserTimeZone());
      return;
    }

    let cancelled = false;
    setLoading(true);

    api
      .get<{ data?: { preferences?: { timezone?: string } } }>('/users/profile')
      .then((res) => {
        if (cancelled) return;
        const tz = res.data?.data?.preferences?.timezone;
        if (typeof tz === 'string' && tz.trim().length > 0) {
          setTimeZone(tz.trim());
        } else {
          setTimeZone(getBrowserTimeZone());
        }
      })
      .catch(() => {
        if (!cancelled) setTimeZone(getBrowserTimeZone());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return { timeZone, loading };
}
